<template>
  <zg-form-element
    :no-margin="noMargin"
    :auto-width="horizontal"
    :full-width="fullWidth"
  >
    <div
      :class="['zg-radio', {
        'zg-radio--has-label': label,
        'zg-radio--has-value': hasValue,
        'zg-radio--is-valid': isValid,
        'zg-radio--is-invalid': invalid,
        'zg-radio--is-focused': focused,
        'zg-radio--is-disabled': disabled,
        'zg-radio--is-read-only': readOnly,
        'zg-radio--horizontal': horizontal && !wrap,
        'zg-radio--vertical': vertical,
        'zg-radio--vertical-mobile': verticalMobile,
        'zg-radio--wrap': wrap,
        'zg-radio--has-tooltip': tooltip && !disabled,
        'zg-radio--has-tooltip-trigger': tooltip && tooltip.trigger && !disabled
      }]"
    >
      <zg-input-legend
        v-if="label"
        :tooltip="tooltip"
        :valid="valid"
        :disabled="disabled"
        :required="showRequired"
        :focused="focused"
      >
        {{ label }}
      </zg-input-legend>

      <div class="wrapper">
        <label
          v-for="(option, key) in formattedOptions"
          :key="key"
          :for="`${name}-${key}`"
          :class="['button', {
            'button--is-selected': isMatching(key, value),
            'button--is-focused': focused && isMatching(key, focusedOption)
          }]"
        >
          <input
            :id="`${name}-${key}`"
            type="radio"
            :name="name"
            :value="key"
            :checked="isMatching(key, value)"
            :disabled="disabled || readOnly"
            @click="onClick"
            @focus="onFocus"
            @blur="onBlur"
            @change="onInput"
          >

          <span>
            {{ option.label }}
          </span>
        </label>
      </div>
    </div>

    <zg-form-error
      :visible="error && invalid"
      type="error"
    >
      {{ error }}
    </zg-form-error>
  </zg-form-element>
</template>

<script>
  import ZgFormElement from '../ZgFormElement/ZgFormElement'
  import ZgFormError from '../ZgFormError/ZgFormError.vue'
  import ZgInputLegend from '../ZgInputLegend/ZgInputLegend.vue'

  export default {
    name: 'ZgRadio',
    components: {
      ZgFormElement,
      ZgInputLegend,
      ZgFormError
    },
    props: {
      name: {
        type: String,
        required: true
      },
      label: {
        type: String,
        required: false,
        default: null
      },
      options: {
        type: Object,
        required: true
      },
      value: {
        type: [String, Number, Boolean],
        required: false,
        default: undefined
      },
      valid: {
        type: Boolean,
        required: false,
        default: false
      },
      invalid: {
        type: Boolean,
        required: false,
        default: false
      },
      error: {
        type: String,
        required: false,
        default: null
      },
      tooltip: {
        type: Object,
        required: false,
        default: null
      },
      horizontal: {
        type: Boolean,
        required: false,
        default: false
      },
      vertical: {
        type: Boolean,
        required: false,
        default: false
      },
      verticalMobile: {
        type: Boolean,
        required: false,
        default: false
      },
      wrap: {
        type: Boolean,
        required: false,
        default: false
      },
      showRequired: {
        type: Boolean,
        required: false,
        default: false
      },
      disabled: {
        type: Boolean,
        required: false,
        default: false
      },
      readOnly: {
        type: Boolean,
        required: false,
        default: false
      },
      noMargin: {
        type: Boolean,
        required: false,
        default: false
      },
      fullWidth: {
        type: Boolean,
        required: false,
        default: false
      }
    },
    data: () => ({
      focused: false,
      focusedOption: undefined
    }),
    computed: {
      hasValue () {
        return !!(this.value || this.value === 0)
      },
      isValid () {
        return !!(this.valid && !this.invalid && !this.disabled)
      },
      formattedOptions () {
        const formatted = {}

        for (const key in this.options) {
          const value = this.options[key]

          formatted[key] = typeof value === 'string'
            ? { label: value, icon: null }
            : value
        }

        return formatted
      }
    },
    methods: {
      isMatching (input1, input2) {
        return input1?.toString() === input2?.toString()
      },
      onClick (e) {
        this.focused = false
        this.focusedOption = undefined
        this.$emit('click', e.target.value)
      },
      onFocus (e) {
        this.focused = true
        this.focusedOption = e.target.value
        this.$emit('focus', e.target.value)
      },
      onBlur (e) {
        this.focused = false
        this.focusedOption = undefined
        this.$emit('blur', e.target.value)
      },
      onInput (e) {
        this.$emit('input', e.target.value)
      }
    }
  }
</script>

<style lang="scss" scoped>
  .zg-radio {
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 100%;

    .wrapper {
      display: flex;
      flex-direction: row;
      align-items: stretch;
      gap: rem(6px);
    }

    .button {
      position: relative;
      width: 100%;
      max-width: 100%;
      height: input(height);
      display: flex;
      align-items: center;
      color: color(gray-500);
      background-color: color(white);
      border: input(border-width) solid color(input-border-default);
      border-radius: radius(input);
      padding: input(padding-y) input(padding-x);
      margin: 0;
      appearance: none;
      transition: all 0.3s ease;
      cursor: pointer;
      z-index: 1;

      input {
        flex-grow: 0;
        flex-shrink: 0;
        margin: 0;
        font: inherit;
        color: currentcolor;
        width: rem(24px);
        height: rem(24px);
        border: 2px solid color(gray-500);
        border-radius: 50%;
        display: grid;
        place-content: center;
        outline: none;
        appearance: none;
        transition: all 0.3s ease;

        &::before {
          content: "";
          width: rem(10px);
          height: rem(10px);
          border-radius: 50%;
          transform: scale(0);
          transition: 120ms transform ease-in-out;
          box-shadow: inset 1em 1em color(black);
        }

        &:checked {
          background-color: color(primary-500);
          border: 2px solid color(primary-500);

          &::before {
            transform: scale(1);
          }
        }
      }

      span {
        @include type-body-m;

        display: inline-flex;
        color: color(black);
        white-space: nowrap;
        margin: 0 0 0 rem(12px);
      }
    }
  }

  // ? ----------------------------------------------------------------------
  // horizontal
  // ? ----------------------------------------------------------------------

  .zg-radio--horizontal {
    @include mq(medium) {
      .wrapper {
        display: flex;
        flex-flow: row nowrap;
      }

      .button {
        width: auto;
      }
    }
  }

  // ? ----------------------------------------------------------------------
  // vertical
  // ? ----------------------------------------------------------------------

  .zg-radio--vertical {
    .wrapper {
      flex-direction: column;
    }
  }

  .zg-radio--vertical-mobile {
    @include mq($until: medium) {
      .wrapper {
        flex-direction: column;
      }
    }
  }

  // ? ----------------------------------------------------------------------
  // wrap
  // ? ----------------------------------------------------------------------

  .zg-radio--wrap {
    .wrapper {
      flex-wrap: wrap;
    }

    .button {
      flex: 0;
    }
  }

  // ? ----------------------------------------------------------------------
  // states
  // ? ----------------------------------------------------------------------

  .zg-radio .button:hover {
    border-color: color(input-border-hover);

    input {
      background-color: color(input-border-hover);
      border-color: color(input-border-hover);
    }
  }

  .zg-radio--is-focused .button.button--is-focused {
    border-color: color(input-border-focus);
    box-shadow: box-shadow(focus);

    &:hover {
      border-color: color(input-border-hover);
    }

    input {
      box-shadow: box-shadow(focus);
    }
  }

  .zg-radio--has-value .button.button--is-selected {
    border-color: color(input-border-default);

    &:hover {
      border-color: color(input-border-hover);
    }
  }

  .zg-radio--is-valid .button.button--is-selected {
    border-color: color(input-border-default);

    &:hover {
      border-color: color(input-border-hover);
    }
  }

  .zg-radio--is-invalid .button {
    border-color: color(input-border-invalid);

    &:hover {
      border-color: color(input-border-invalid);
    }

    input {
      border-color: color(input-border-invalid);
    }
  }

  .zg-radio--is-invalid .button.button--is-focused {
    box-shadow: box-shadow(error);
  }

  .zg-radio--is-disabled .button,
  .zg-radio--is-disabled .button.button.button--is-selected {
    opacity: 0.5;
    border-color: color(input-border-disabled);
    cursor: not-allowed;

    &:hover {
      border-color: color(input-border-disabled);
    }

    input {
      cursor: not-allowed;
    }
  }

  .zg-radio--is-read-only .button,
  .zg-radio--is-read-only .button.button.button--is-selected {
    border-color: color(input-border-disabled);
    cursor: not-allowed;

    &:hover {
      border-color: color(input-border-disabled);
    }

    input {
      cursor: not-allowed;
    }
  }
</style>
